import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import QuizContent from '../shared/QuizContent';
import QuizTitle from '../shared/QuizTitle';
import QuizOptions from '../shared/QuizOptions';
import QuizOption from '../shared/QuizOption';
import LazyImage from '../shared/LazyImage';
import { getQuizOptionsData } from '../../constants';
import { useVideoAnimation } from '../../hooks/useVideoAnimation';
import {
  ethnicityKey,
  useGrowthBookValues,
} from '../../contexts/GrowthBookContext';

const Step2 = ({ onAnswer, onPrevious, selections, isTransitioning }) => {
  const { t } = useTranslation();
  const { getVideoProps } = useVideoAnimation();
  const { videos: gbVideos } = useGrowthBookValues();
  const personalities = getQuizOptionsData('personality');

  // Step 2 videos are split by the Step 1 ethnicity pick — `asian` vs
  // `caucasian` branches in the GB config. Empty/missing URL → keep showing
  // the option's image as before.
  const eKey = ethnicityKey(selections?.step1?.ethnicity);
  const resolveVideoSrc = (option) =>
    (gbVideos?.step2?.[eKey]?.[option.value] || '').trim();

  const [selected, setSelected] = useState(
    selections?.step2?.personality || personalities[0]?.value || '',
  );

  const handleClick = useCallback(
    (value) => {
      if (isTransitioning) return;
      setSelected(value);
    },
    [isTransitioning],
  );

  const handleContinue = useCallback(() => {
    if (isTransitioning || !selected) return;
    onAnswer('step2', { personality: selected });
  }, [selected, onAnswer, isTransitioning]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;
    onPrevious();
  }, [onPrevious, isTransitioning]);

  return (
    <div className={`quizContentWrapper ${isTransitioning ? 'no-click' : ''}`}>
      <QuizContent className="step2-design">
        <div className="step2-block">
          <QuizTitle>
            {t('choose')}{' '}
            <span>{t('personality_text')}</span>
          </QuizTitle>
          <QuizOptions className="personality-traits-options">
            {personalities.map((option) => {
              const isSelected = selected === option.value;
              const videoSrc = resolveVideoSrc(option);
              return (
                <QuizOption
                  key={option.value}
                  value={option.value}
                  name="personality"
                  onClick={() => handleClick(option.value)}
                  className={`personality-trait-option ${
                    isSelected
                      ? 'selected'
                      : selected
                      ? 'not-selected'
                      : ''
                  } even`}
                >
                  <div className="personality-trait-image">
                    {videoSrc ? (
                      <video
                        src={videoSrc}
                        {...getVideoProps()}
                        controls={false}
                        playsInline
                        webkit-playsinline=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <LazyImage src={option.image} alt={option.alt} />
                    )}
                  </div>
                  <div className="image-title">{t(option.text)}</div>
                </QuizOption>
              );
            })}
          </QuizOptions>
        </div>

        <div className="navigation-wrapper step2-navigation-wrapper">
          <button
            className="previous-button"
            onClick={handlePrevious}
            disabled={isTransitioning}
          >
            {t('previous')}
          </button>
          <button
            className="continue-button"
            onClick={handleContinue}
            disabled={!selected || isTransitioning}
          >
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  );
};

export default Step2;
